import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useBanner } from "../context/BannerContext";
import { useLoading } from "../context/LoadingContext";
import { useUserActions } from "../hooks/useUserActions";
import { useExportActions } from "../hooks/useExportActions";
import { useDatasources } from "../context/DatasourceContext";
import { isEdiphiUser, isUserAdmin } from "../../clients/auth";
import UserCard from "../components/settings/UserCard";
import DividerAccordionLayout from "../shared/accordions/DividerAccordionLayout";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import ProcoreExportDialog from "../components/settings/ProcoreExportDialog";
import CurrencyDialog from "../components/settings/CurrencyDialog";
import { ValidationError } from "../../errors";
import type { Currency, CurrencyScope } from "../../excel/userTools/currency";
import type { ProcoreExportOptions } from "../../integrations/exports/procore";
import CardActionButton from "../shared/buttons/CardActionButton";
import { FLATTEN_TABLE_FORMULAS_LABEL, FLATTEN_TABLE_FORMULAS_TOOLTIP } from "../../excel/userTools/flattenFormulas";
import {
  ADMIN_TOOLS_HEADING,
  BUDGET_IMPORT_SUCCESS,
  CHANGE_CURRENCY_ERROR,
  CHANGE_CURRENCY_LABEL,
  CHANGE_CURRENCY_NONE_FOUND,
  CREATE_BUDGET_IMPORT_LABEL,
  CREATE_JOIN_IMPORT_LABEL,
  CREATE_PROCORE_IMPORT_LABEL,
  EDIPHI_TOOLBOX_HEADING,
  FLATTEN_FORMULAS_ERROR,
  JOIN_IMPORT_SUCCESS,
  LOAD_LABOR_CREWS_TABLE_LABEL,
  LOAD_MASTERFORMAT_TABLE_LABEL,
  LOAD_UNIFORMAT_TABLE_LABEL,
  LOAD_UPC_TABLE_LABEL,
  LOAD_USERS_TABLE_LABEL,
  MASTERFORMAT_LOAD_ERROR,
  PROCORE_IMPORT_ERROR,
  PROCORE_IMPORT_SUCCESS,
  PRINT_LAYOUT_SAVE_ERROR,
  PRINT_LAYOUT_UNSUPPORTED,
  SAVE_PRINT_LAYOUT_LABEL,
  SAVE_PRINT_LAYOUT_TOOLTIP,
  SORT_FIELDS_LOAD_ERROR,
  TOOL_BOX_HEADING,
  UNIFORMAT_LOAD_ERROR,
  UPC_LABOR_CREWS_LOAD_ERROR,
  UPC_LOAD_ERROR,
  USERS_LOAD_ERROR,
  currencyChanged,
  currencySheetsSkipped,
  masterFormatCreatedIn,
  printLayoutCleared,
  printLayoutFailed,
  printLayoutSaved,
  printLayoutTooLong,
  uniformatCreatedIn,
  upcLaborCrewsLoadedIn,
  upcLoadedIn,
  usersCreatedIn,
} from "../constants";
import { getActiveWorksheetName } from "../../excel/workbook";

interface UserSettingsPageProps {
  onLogOut: () => void;
}

const UserSettingsPage: React.FC<UserSettingsPageProps> = ({ onLogOut }) => {
  const { showBanner } = useBanner();
  const { withLoading } = useLoading();
  const {
    handleLoadUPC,
    handleLoadUPCLaborCrews,
    handleLoadMasterFormat,
    handleLoadUniformat,
    handleLoadUsers,
    handleFlattenTableFormulas,
    handleChangeCurrency,
    handleSavePrintLayout,
  } = useUserActions();
  const { createBudgetImport, createJoinExport, createProcoreExport } = useExportActions();
  const { hasDatasource } = useDatasources();
  const budgetImportEnabled = useFeatureFlag("last-mile-enable-budget-import");
  const joinExportEnabled = useFeatureFlag("last-mile-enable-join-export");
  const procoreExportEnabled = useFeatureFlag("last-mile-enable-procore-export");
  const [procoreDialogOpen, setProcoreDialogOpen] = useState<boolean>(false);
  const [currencyDialogOpen, setCurrencyDialogOpen] = useState<boolean>(false);
  const [activeSheetName, setActiveSheetName] = useState<string>("");
  /** Which tool is running, so its button alone shows the spinner. */
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    void refreshActiveSheetName();
  }, []);

  /** Each action reports its own outcome by banner, so this only tracks which one is in flight. */
  const runTool = async (key: string, action: () => Promise<void>) => {
    setPending(key);
    try {
      await action();
    } finally {
      setPending(null);
    }
  };

  const refreshActiveSheetName = async () => {
    try {
      setActiveSheetName(await getActiveWorksheetName());
    } catch {
      // on failure, the button sublabel for active sheet will be empty ""
    }
  };

  const flattenTableFormulas = async () => {
    try {
      // Keep the button sublabel in sync without the extra used-range load.
      await refreshActiveSheetName();

      const stats = await withLoading(async () => await handleFlattenTableFormulas());
      showBanner(
        `Created "${stats.newSheetName}". Scanned ${stats.cellsScanned.toLocaleString()} cells; flattened ${stats.cellsFlattened.toLocaleString()}.`,
        "success"
      );
    } catch (error) {
      showBanner(FLATTEN_FORMULAS_ERROR, "error");
    }
  };

  const openCurrencyDialog = async () => {
    await refreshActiveSheetName();
    setCurrencyDialogOpen(true);
  };

  const changeCurrency = async (currency: Currency, scope: CurrencyScope) => {
    try {
      const stats = await withLoading(async () => await handleChangeCurrency(currency.id, scope));
      const skipped = stats.sheetsSkipped > 0 ? ` ${currencySheetsSkipped(stats.sheetsSkipped)}` : "";
      if (stats.cellsChanged === 0) {
        showBanner(`${CHANGE_CURRENCY_NONE_FOUND}.${skipped}`, "info");
        return;
      }
      const where =
        scope === "activeSheet"
          ? `on ${stats.sheetName}`
          : `across ${stats.sheetsScanned} sheet${stats.sheetsScanned === 1 ? "" : "s"}`;
      showBanner(`${currencyChanged(stats.cellsChanged, currency.symbol, where)}.${skipped}`, "success");
    } catch (error) {
      showBanner(CHANGE_CURRENCY_ERROR, "error");
    }
  };

  const loadUPC = async () => {
    try {
      let duration = 0;
      await withLoading(async () => {
        duration = await handleLoadUPC();
      });
      showBanner(upcLoadedIn(duration.toFixed(1)), "success");
    } catch (error) {
      showBanner(UPC_LOAD_ERROR, "error");
    }
  };

  const loadUPCLaborCrews = async () => {
    try {
      let duration = 0;
      await withLoading(async () => {
        duration = await handleLoadUPCLaborCrews();
      });
      showBanner(upcLaborCrewsLoadedIn(duration.toFixed(1)), "success");
    } catch (error) {
      // A missing region or an empty catalog says what to do; anything else gets the generic message.
      showBanner(error instanceof ValidationError ? error.message : UPC_LABOR_CREWS_LOAD_ERROR, "error");
    }
  };

  const loadMasterFormat = async () => {
    try {
      let duration = 0;
      await withLoading(async () => {
        duration = await handleLoadMasterFormat();
      });
      showBanner(masterFormatCreatedIn(duration.toFixed(1)), "success");
    } catch (error) {
      showBanner(MASTERFORMAT_LOAD_ERROR, "error");
    }
  };

  const loadUniformat = async () => {
    try {
      let duration = 0;
      await withLoading(async () => {
        duration = await handleLoadUniformat();
      });
      showBanner(uniformatCreatedIn(duration.toFixed(1)), "success");
    } catch (error) {
      showBanner(UNIFORMAT_LOAD_ERROR, "error");
    }
  };

  const loadUsers = async () => {
    try {
      let duration = 0;
      await withLoading(async () => {
        duration = await handleLoadUsers();
      });
      showBanner(usersCreatedIn(duration.toFixed(1)), "success");
    } catch (error) {
      showBanner(USERS_LOAD_ERROR, "error");
    }
  };

  const savePrintLayout = async () => {
    try {
      await refreshActiveSheetName();
      const result = await withLoading(async () => await handleSavePrintLayout());
      switch (result.outcome) {
        case "unsupported":
          showBanner(PRINT_LAYOUT_UNSUPPORTED, "error");
          break;
        case "tooLong":
          showBanner(printLayoutTooLong(result.sheetName, result.section), "error");
          break;
        case "cleared":
          showBanner(printLayoutCleared(result.sheetName), "info");
          break;
        case "saved": {
          const saved = printLayoutSaved(
            result.sheetName,
            result.slots.map(({ section, position }) => `${section} ${position}`)
          );
          if (result.failedSheets.length > 0) {
            showBanner(`${saved}. ${printLayoutFailed(result.failedSheets)}`, "warn");
          } else {
            showBanner(saved, "success");
          }
          break;
        }
      }
    } catch (error) {
      showBanner(PRINT_LAYOUT_SAVE_ERROR, "error");
    }
  };

  const createBudgetImportFile = async () => {
    try {
      await withLoading(async () => {
        await createBudgetImport();
        showBanner(BUDGET_IMPORT_SUCCESS, "success");
      });
    } catch (error) {
      showBanner(error instanceof Error ? error.message : "Failed to create budget import", "error");
    }
  };

  const createProcoreExportFile = async (opts: ProcoreExportOptions) => {
    setProcoreDialogOpen(false);
    try {
      await withLoading(async () => {
        await createProcoreExport(opts);
        showBanner(PROCORE_IMPORT_SUCCESS, "success");
      });
    } catch (error) {
      showBanner(error instanceof Error ? error.message : PROCORE_IMPORT_ERROR, "error");
    }
  };

  const createJoinImportFile = async () => {
    try {
      await withLoading(async () => {
        await createJoinExport();
        showBanner(JOIN_IMPORT_SUCCESS, "success");
      });
    } catch (error) {
      showBanner(error instanceof Error ? error.message : "Failed to create Join import file", "error");
    }
  };

  const adminTools = [
    { key: "upc", label: LOAD_UPC_TABLE_LABEL, action: loadUPC },
    { key: "upcLaborCrews", label: LOAD_LABOR_CREWS_TABLE_LABEL, action: loadUPCLaborCrews },
    { key: "masterFormat", label: LOAD_MASTERFORMAT_TABLE_LABEL, action: loadMasterFormat },
    { key: "uniformat", label: LOAD_UNIFORMAT_TABLE_LABEL, action: loadUniformat },
    { key: "users", label: LOAD_USERS_TABLE_LABEL, action: loadUsers },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 2.5, width: "100%" }}>
      <UserCard onLogOut={onLogOut} />

      <DividerAccordionLayout title={TOOL_BOX_HEADING} collapsible={false}>
        <CardActionButton
          label={FLATTEN_TABLE_FORMULAS_LABEL}
          subLabel={activeSheetName}
          onClick={() => void runTool("flatten", flattenTableFormulas)}
          loading={pending === "flatten"}
          icon={InfoOutlinedIcon}
          iconTooltip={FLATTEN_TABLE_FORMULAS_TOOLTIP}
          disableOutline
        />
        <CardActionButton
          label={CHANGE_CURRENCY_LABEL}
          onClick={() => void openCurrencyDialog()}
          loading={pending === "currency"}
          disableOutline
        />
        {hasDatasource("ediphi", "estimate") && (
          <>
            {budgetImportEnabled && (
              <CardActionButton
                label={CREATE_BUDGET_IMPORT_LABEL}
                onClick={() => void runTool("budgetImport", createBudgetImportFile)}
                loading={pending === "budgetImport"}
                disableOutline
              />
            )}
            {joinExportEnabled && (
              <CardActionButton
                label={CREATE_JOIN_IMPORT_LABEL}
                onClick={() => void runTool("joinExport", createJoinImportFile)}
                loading={pending === "joinExport"}
                disableOutline
              />
            )}
            {procoreExportEnabled && (
              <CardActionButton
                label={CREATE_PROCORE_IMPORT_LABEL}
                onClick={() => setProcoreDialogOpen(true)}
                disableOutline
              />
            )}
          </>
        )}
      </DividerAccordionLayout>

      {isUserAdmin() && (
        <DividerAccordionLayout title={ADMIN_TOOLS_HEADING} collapsible={false}>
          {adminTools.map(({ key, label, action }) => (
            <CardActionButton
              key={key}
              label={label}
              onClick={() => void runTool(key, action)}
              loading={pending === key}
              disableOutline
            />
          ))}
        </DividerAccordionLayout>
      )}

      {isEdiphiUser() && (
        <DividerAccordionLayout title={EDIPHI_TOOLBOX_HEADING} collapsible={false}>
          <CardActionButton
            label={SAVE_PRINT_LAYOUT_LABEL}
            subLabel={activeSheetName}
            onClick={() => void runTool("printLayout", savePrintLayout)}
            loading={pending === "printLayout"}
            icon={InfoOutlinedIcon}
            iconTooltip={SAVE_PRINT_LAYOUT_TOOLTIP}
            disableOutline
          />
        </DividerAccordionLayout>
      )}

      {currencyDialogOpen && (
        <CurrencyDialog
          activeSheetName={activeSheetName}
          onSubmit={(currency, scope) => {
            setCurrencyDialogOpen(false);
            void runTool("currency", () => changeCurrency(currency, scope));
          }}
          onClose={() => setCurrencyDialogOpen(false)}
        />
      )}

      {procoreDialogOpen && (
        <ProcoreExportDialog
          onSubmit={(opts) => void runTool("procoreExport", () => createProcoreExportFile(opts))}
          onClose={() => setProcoreDialogOpen(false)}
          onLoadFailed={() => {
            setProcoreDialogOpen(false);
            showBanner(SORT_FIELDS_LOAD_ERROR, "error");
          }}
        />
      )}
    </Box>
  );
};

export default UserSettingsPage;
